import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {useGetLink}  from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    linkText = "linkText",
    textLink = "textLink",
    openNewTab = "openNewTab",
}

interface Props extends WidgetProps {
    fields: {
        [fields.background]: "image";
        [fields.image]: string;
        [fields.linkText]: string;
        [fields.textLink]: LinkFieldValue;
        [fields.openNewTab]: boolean;
    };
}

export interface ImageLinkBoxStyles {
    wrapperStyles?: InjectableCss;
    imageWrapper?: InjectableCss;
    headingStyles?: InjectableCss;
}

export const imageLinkBoxStyles: ImageLinkBoxStyles = {
    wrapperStyles: {
        css: css`
            display: flex;
            background: ${getColor("primary.contrast")};
            margin: 10px;
            flex-direction: column;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            > div {
                width: 100%;
                height: 250px;
                img {
                    object-fit: cover;
                    object-position: center;
                    overflow: hidden;
                    height: 100%;
                }
            }
        `,
    },

    headingStyles: {
        css: css`
            align-items: center;
            display: flex;
            flex-direction: column;
            h3 {
                margin: 1rem 0;
            }
            button[class^="StyledButton"]{
                span[class^="TypographyStyle"]{
                    display: flex;
                    color: #364e63;
                    font-size: 14px;
                    font-weight: 600;
                }
            }
        `,
    },
};

const styles = imageLinkBoxStyles;

export const ImageLinkBox: React.FC<Props> = props => {
    const { url } = useGetLink(props.fields.textLink);
    const history = useHistory();
    const onClick = () => {
        if (!url) {
            return;
        }
        if (url.startsWith("http")) {
            window.open(url, "_blank");
        } else {
            props.fields.openNewTab ? window.open(url, "_blank") : history.push(url);
        }
    };

    return (
        <StyledWrapper {...styles.wrapperStyles}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={props.fields.image}></LazyImage>
            </StyledWrapper>
            <StyledWrapper {...styles.headingStyles}>
                    <Link onClick={onClick}>
                        {props.fields.linkText}
                        <Icon src={ChevronRight} size={20} />
                    </Link>
            </StyledWrapper>            
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ImageLinkBox,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [{ displayName: "Image", value: "image" }],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Translatable",
                name: fields.linkText,
                editorTemplate: "TextField",
                displayName: "Text Label",
                defaultValue: "",
            },
            {
                name: fields.openNewTab,
                displayName: "Open in a new tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.textLink,
                displayName: "Text Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
